import { EventEmitter } from "@angular/core";
import { DataActions } from "@app/project/store/actions";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { ActionWord } from "@app/shared/resources/action-word";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionCreator } from "@ngrx/store";
import { Action, TypedAction } from "@ngrx/store/src/models";
import { Observable } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { ActionPayloadGeneric } from "../actions";

export type RequestAction<Payload> = ActionCreator<string, (props: Payload) => Payload & TypedAction<string>>;
export type SuccessAction<Result> = ActionCreator<string, (props: Result) => Result & TypedAction<string>>;
export type FailureAction<Payload> = ActionCreator<
    string,
    (props: DataServiceError<Payload>) => DataServiceError<Payload> & TypedAction<string>
>;
interface ActionSet<Payload, Result> {
    request: RequestAction<Payload>;
    success: SuccessAction<Result>;
    failure: FailureAction<Payload>;
}

type DeleteActionSet<Payload, Result> = ActionSet<Payload, Result> & {
    deleteConfirmation: RequestAction<Payload>;
    deleteConfirmationDismiss: RequestAction<Payload>;
};

export class EffectsUtils {
    constructor(protected readonly actions$: Actions) {}

    createFailureEffect = <Payload>(
        action: FailureAction<Payload>,
        actionWord: ActionWord,
        typeName?: string,
        customErrorMessage?: string
    ) =>
        createEffect(() =>
            this.actions$.pipe(ofType(action), map(DataActions.handleErrorMessage2(actionWord, typeName, customErrorMessage)))
        );

    createSuccessEffect = <Result>(action: SuccessAction<Result>, actionWord: ActionWord, typeName?: string, path = null) =>
        createEffect(() => this.actions$.pipe(ofType(action), map(DataActions.handleSuccessMessage2(actionWord, path, typeName))));

    toAction<ResultType, PayloadType>(
        dbMethod: Observable<ResultType>,
        actionSet: ActionSet<PayloadType, ResultType>,
        callbackEmitter: EventEmitter<Action>
    ) {
        return DataActions.toAction2()(dbMethod, actionSet.success, actionSet.failure, callbackEmitter);
    }

    createExhaustEffect = <PayloadType, ResultType>({
        dbMethod,
        actionSet,
    }: {
        dbMethod: (actionProps: PayloadType) => Observable<ResultType>;
        actionSet: ActionSet<PayloadType & ActionPayloadGeneric, ResultType>;
    }) =>
        createEffect(() =>
            this.actions$.pipe(
                ofType(actionSet.request),
                exhaustMap(actionProps => this.toAction(dbMethod(actionProps), actionSet, actionProps.callbackEmitter)),
                catchError(DataActions.toError2(actionSet.failure))
            )
        );

    createSwitchEffect = <PayloadType, ResultType>({
        dbMethod,
        actionSet,
    }: {
        dbMethod: (actionProps: PayloadType) => Observable<ResultType>;
        actionSet: ActionSet<PayloadType & ActionPayloadGeneric, ResultType>;
    }) =>
        createEffect(() =>
            this.actions$.pipe(
                ofType(actionSet.request),
                switchMap(actionProps => this.toAction(dbMethod(actionProps), actionSet, actionProps.callbackEmitter)),
                catchError(DataActions.toError2(actionSet.failure))
            )
        );

    createDeleteConfirmationEffect = <PayloadType, ResultType>({
        actionSet,
        dbMethod,
        confirmationService,
    }: {
        actionSet: DeleteActionSet<PayloadType, ResultType>;
        dbMethod: (actionProps: PayloadType) => Observable<{ payload: any; display: string }>;
        confirmationService: DeleteConfirmationService;
    }) =>
        createEffect(() =>
            this.actions$.pipe(
                ofType(actionSet.deleteConfirmation),
                exhaustMap(actionProps =>
                    dbMethod(actionProps).pipe(
                        exhaustMap(({ payload, display }) =>
                            confirmationService.open$(payload, actionSet.deleteConfirmationDismiss, actionSet.request, display)
                        )
                    )
                )
            )
        );
}
